/**
 * ChartPip — a second panel inside the chart, showing what the chart cannot.
 *
 * A frame with a content slot rather than a mini chart. `chart` draws the
 * timeframe above this one; the rest are facts about the symbol, and every one
 * of them is a module the data panel will render at full size. Written once,
 * hosted twice — which is the whole reason the PIP and the data panel are one
 * feature and not two.
 *
 * ## It is drawn with a canvas, not a second SciChart surface
 *
 * `docs/features/charts.md` records what a second surface costs: the div needs a
 * unique `id` or the new one deletes the first's render destination, the
 * `modifierGroup` must not be shared or a disposed surface freezes the other
 * chart's crosshair for good, and `delete()` mutates the div it was handed.
 * That is a great deal of care for a thumbnail with no axes and no interaction.
 * The geometry is `lib/pip-render.ts`, tested in Node; this file only strokes it.
 *
 * ## Placement is measured on the running chart
 *
 * `placePip` decides the corner by counting how many visible closes fall in each
 * candidate box, so it needs real rectangles — which only the browser has. The
 * container is measured with a `ResizeObserver`, and the value legend's block by
 * its `data-qf-legend-pane` attribute, because the top-left position sits under
 * it and a chart with seven plots makes a taller legend than one with three.
 * The legend is watched rather than read once (`use-legend-height.ts`): it
 * appears after the bars, and a single read kept 0 and covered it.
 *
 * ## Everything it does is on its own bar
 *
 * Content, interval, corner, size, swap and close. A PIP configured from a menu
 * somewhere else would be a panel whose settings are further away than the panel
 * is, and the bar is already on screen whenever the thing it configures is.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ChartGroup, Frequency } from "@qufolio/model";

import { useTheme, useTokenResolver } from "./theme";
import { candleLayout, type Bar } from "../lib/pip-render";
import { useLegendHeight } from "./use-legend-height";
import { pipFrame, placePip, SIDE_INSET, type PlacedCorner } from "../lib/pip-placement";
import {
  PIP_CORNERS,
  PIP_FRACTIONS,
  PIP_SIZES,
  nextCoarser,
  pipViewsFor,
  viewAvailableFor,
  pipFrequencyFor,
  type PipCorner,
  type PipSize,
  type PipView,
} from "../lib/pip";
import { can } from "../lib/entitlements";
import { intervalLabel } from "../lib/interval-label";
import { symbolView } from "./symbol-views";
import { Menu, MenuRow } from "./Menu";
import { fillRow, CHIP_GAP, type OneRow } from "../lib/chip-rows";

/**
 * How a view is written on the chip.
 *
 * From the registry for everything but `chart`, so the panel's tab and the PIP's
 * chip cannot end up calling the same view two different things. `chart` has no
 * registry entry because only a PIP can show one.
 */
function viewLabel(view: PipView): string {
  return view === "chart" ? "Chart" : (symbolView(view)?.label ?? view);
}

export interface ChartPipProps {
  group: ChartGroup;
  /** Bars for the `chart` view — already the PIP's own timeframe. */
  bars: readonly Bar[];
  /** Visible closes of the *chart*, which is what the placement rule counts. */
  closes: readonly number[];
  /** Top of the price pane as a percentage — not 0 when a lane sits above it. */
  priceBandTop: number;
  /** Bottom of the price pane as a percentage, from `stackFractions`. */
  priceBandBottom: number;
  /** Price pane id, so the legend block to measure can be found. */
  pricePaneId: string | undefined;
  onView: (view: PipView) => void;
  onFrequency: (frequency: Frequency) => void;
  onCorner: (corner: PipCorner) => void;
  onSize: (size: PipSize) => void;
  /** Promote the PIP's interval to the chart's, or `undefined` when it cannot. */
  onSwap: (() => void) | undefined;
  onClose: () => void;
}

export function ChartPip({
  group,
  bars,
  closes,
  priceBandTop,
  priceBandBottom,
  pricePaneId,
  onView,
  onFrequency,
  onCorner,
  onSize,
  onSwap,
  onClose,
}: ChartPipProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState<{ width: number; height: number } | null>(null);
  const legendH = useLegendHeight(hostRef, pricePaneId);

  const view = group.pipView;
  const size = group.pipSize ?? "m";
  const corner = group.pipCorner ?? "auto";

  // The container's own size, which is the only thing that can say what a
  // fraction of the pane is in pixels. A `ResizeObserver` rather than a resize
  // listener: a pane divider drag changes this without the window moving at all.
  useLayoutEffect(() => {
    const host = hostRef.current?.parentElement;
    if (!host) return;
    const read = () => setBox({ width: host.clientWidth, height: host.clientHeight });
    read();
    const ro = new ResizeObserver(read);
    ro.observe(host);
    return () => ro.disconnect();
  }, []);

  if (!view || !box || box.width === 0 || box.height === 0) return <div ref={hostRef} hidden />;

  // What is actually on screen, which is the stored view unless this symbol's
  // market has no source for it — see `viewAvailableFor`. `chart` always
  // survives the filter, so this can never be undefined.
  const shown = viewAvailableFor(view, group.symbol) ? view : "chart";

  // The price pane is what the panel lives in, so its height is what a size
  // fraction is a fraction *of* — a PIP sized against the whole chart would
  // swallow a short price pane on a four-pane layout. And its top is where the
  // panel starts: a signal lane stacked above price pushes the legend down.
  const frame = pipFrame(
    box,
    priceBandTop / 100,
    priceBandBottom / 100,
    legendH,
    PIP_FRACTIONS[size],
  );
  const w = frame.width;
  const h = frame.height;
  const topY = frame.topLeft.y;
  const bottomY = frame.bottomLeft.y;
  const plot = frame.plot;
  const topBox = frame.topLeft;
  const bottomBox = frame.bottomLeft;

  const placed: PlacedCorner | PipCorner =
    corner === "auto" ? placePip(closes, plot, topBox, bottomBox) : corner;

  const position: React.CSSProperties =
    placed === "topLeft"
      ? { left: SIDE_INSET, top: topY }
      : placed === "bottomLeft"
        ? { left: SIDE_INSET, top: bottomY }
        : placed === "topRight"
          ? { right: SIDE_INSET, top: topY }
          : { right: SIDE_INSET, top: bottomY };

  return (
    <div ref={hostRef} style={{ ...HOST, ...position, width: w, height: h }}>
      <PipHeader
        group={group}
        view={shown}
        size={size}
        corner={corner}
        onView={onView}
        onFrequency={onFrequency}
        onCorner={onCorner}
        onSize={onSize}
        onSwap={onSwap}
        onClose={onClose}
      />
      <div style={BODY}>
        {/* `shown`, not `view`: `pipView` is workspace data, so a chart saved on
            News and later pointed at an India symbol arrives here asking for a
            feed that has none. Falling back to the candles rather than writing
            the workspace — the choice is still theirs, and charting a US symbol
            again honours it. */}
        {shown === "chart" ? (
          <PipCandles bars={bars} width={w} height={h - HEADER_H} />
        ) : (
          // The guard the canvas needs too: the surface underneath runs
          // `ZoomPanModifier`, so without it a scroll or a drag inside the panel
          // pans the chart behind it.
          <div style={FILL} onPointerDown={(e) => e.stopPropagation()}>
            <PipView view={shown} symbol={group.symbol} />
          </div>
        )}
      </div>
    </div>
  );
}

// 22 until the type inside it grew: the chips are 18 tall now, and a 22px row
// clipped their borders top and bottom.
const HEADER_H = 26;

/**
 * The header row's flex gap, in px.
 *
 * A constant rather than a literal because **two places have to agree**: the
 * style that lays the row out, and `PipHeader`'s budget arithmetic, which adds
 * the chevron's width *and this gap* back into the strip's measured width. They
 * disagreed — the style said 3, the arithmetic used `CHIP_GAP` (6) — so the
 * budget grew by 3px exactly when the chevron appeared, and a width inside that
 * window never settled.
 */
const HEADER_GAP = 3;

/**
 * The bar, and every setting the panel has.
 *
 * The content and interval chips **cycle** rather than opening a menu. Six views
 * and four rungs are short lists, the panel is 170px wide, and a popover
 * measured off a control inside an overlay inside a clipping context is a great
 * deal of machinery for a choice that is one click away either way.
 */
function PipHeader({
  group,
  view,
  size,
  corner,
  onView,
  onFrequency,
  onCorner,
  onSize,
  onSwap,
  onClose,
}: {
  group: ChartGroup;
  view: PipView;
  size: PipSize;
  corner: PipCorner;
  onView: (view: PipView) => void;
  onFrequency: (frequency: Frequency) => void;
  onCorner: (corner: PipCorner) => void;
  onSize: (size: PipSize) => void;
  onSwap: (() => void) | undefined;
  onClose: () => void;
}) {
  const shown = pipFrequencyFor(group.frequency, group.pipFrequency);
  const cycle = <T,>(list: readonly T[], current: T, set: (next: T) => void) => {
    const i = list.indexOf(current);
    set(list[(i + 1) % list.length]!);
  };

  // The chips this symbol has a source for. `PIP_VIEWS` minus whatever its
  // market cannot answer — and `fit` holds *indices*, so every reader below has
  // to index this same array or the chevron lists the wrong chip.
  //
  // Memoised because `measure` depends on it and the resize observer depends on
  // `measure`: a fresh array each render would tear down and re-attach the
  // observer on every render, which is the shape of a loop rather than a cost.
  // **`can` is a dependency, not just the symbol.** `viewAvailableFor` now asks
  // the plan as well as the market, and entitlements land with `/auth/me` after
  // this has already mounted — a memo keyed on the symbol alone would hold the
  // permissive answer until the user charted something else.
  const summary = can("feat:analyst");
  const views = useMemo(() => pipViewsFor(group.symbol), [group.symbol, summary]);

  const strip = useRef<HTMLDivElement>(null);
  const ruler = useRef<HTMLDivElement>(null);
  const moreBtn = useRef<HTMLButtonElement | null>(null);
  const [fit, setFit] = useState<OneRow>({ shown: views.map((_, i) => i), hidden: [] });
  const [more, setMore] = useState(false);

  /**
   * How many view chips fit, measured — the group bar's arrangement exactly.
   *
   * The strip scrolled sideways before this, which fits six chips into 170px by
   * putting four of them where nobody looks. A chevron says how many are hidden
   * and gives them a list; a scroll region says nothing and has to be discovered.
   *
   * Every load-bearing detail here is `GroupBar`'s, and each is a silent failure
   * on its own:
   *
   * - **An off-screen ruler that always holds every chip.** Measuring the strip
   *   itself reads the DOM left by the previous render, which contains only the
   *   chips that were shown — so from a cold start there is nothing to measure,
   *   the count stays at nothing, and the header latches to a lone chevron.
   * - **A ruler chip renders the same markup as a real one**, or the widths
   *   describe something that is not on screen.
   * - **The chevron's width is added back into the budget.** It sits beside the
   *   strip, so the strip is already narrower for it; subtracting a reservation
   *   as well would charge for the same button twice. `fillRow` reserves for it
   *   exactly when it is needed.
   * - **Zero width means "no layout yet", not "no room".** jsdom reports zero
   *   for everything, and so does a real browser for a frame before first paint
   *   and while the PIP is hidden. Showing everything is the honest answer, and
   *   the next measurement corrects it.
   */
  const measure = useCallback(() => {
    const row = strip.current;
    const rule = ruler.current;
    if (!row || !rule) return;
    // **`HEADER_GAP`, not `CHIP_GAP`** — the chevron is a sibling in `HEADER`,
    // so it is *that* row's gap the strip gives up for it. Adding back a
    // different number makes the budget depend on whether the chevron happens
    // to be rendered, which is the one thing this arithmetic decides: with
    // `CHIP_GAP` (6) against a header gap of 3, the budget was 3px larger
    // whenever the chevron existed, and any width inside that 3px window
    // oscillated forever — no chevron, so it does not fit, so a chevron, so it
    // fits, so no chevron. React counts that as runaway nested updates and
    // kills the app with error #185. It is only reachable while dragging a
    // divider, because that is what sweeps the width across the window.
    const budget = row.clientWidth + (moreBtn.current ? moreBtn.current.offsetWidth + HEADER_GAP : 0);
    const widths = Array.from(rule.children).map((c) => (c as HTMLElement).offsetWidth);
    const all = views.map((_, i) => i);
    // The showing view is never behind the chevron: a header whose selected chip
    // is hidden does not say what the panel is showing.
    const active = views.indexOf(view);
    const next =
      budget > 0
        ? fillRow(widths, budget, { required: active >= 0 ? [active] : [] })
        : { shown: all, hidden: [] };
    setFit((prev) =>
      prev.shown.join() === next.shown.join() && prev.hidden.join() === next.hidden.join()
        ? prev
        : next,
    );
  }, [view, views]);

  useLayoutEffect(() => {
    measure();
  });

  useEffect(() => {
    const row = strip.current;
    if (!row) return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(row);
    return () => observer.disconnect();
  }, [measure]);

  return (
    <div style={HEADER}>
      {/* The ruler: every chip, off-screen, same markup as a real one. Not
          `display: none`, which has no geometry at all. */}
      <div ref={ruler} aria-hidden style={RULER}>
        {views.map((v) => (
          // `active` mirrored, not omitted: the showing chip is `fontWeight:
          // 600` and therefore wider than the same word at 400. A ruler that
          // measured them all light would under-measure exactly one chip — the
          // one that is guaranteed to be on screen — and the strip would clip
          // its last chip only while certain views were selected.
          <Chip key={v} label={viewLabel(v)} title="" active={v === view} onClick={() => {}} />
        ))}
      </div>
      <div ref={strip} style={VIEW_STRIP}>
        {fit.shown.map((i) => {
          const v = views[i]!;
          return (
            <Chip
              key={v}
              label={viewLabel(v)}
              title={`Show ${viewLabel(v)}`}
              active={v === view}
              onClick={() => onView(v)}
            />
          );
        })}
      </div>
      {fit.hidden.length > 0 && (
        <>
          <Chip
            chipRef={moreBtn}
            label={`+${fit.hidden.length} ▾`}
            title={`${fit.hidden.length} more`}
            onClick={() => setMore((open) => !open)}
          />
          {more && (
            <Menu anchorRef={moreBtn} align="right" onClose={() => setMore(false)}>
              {fit.hidden.map((i) => {
                const v = views[i]!;
                return (
                  <MenuRow
                    key={v}
                    active={v === view}
                    onSelect={() => {
                      onView(v);
                      setMore(false);
                    }}
                  >
                    {viewLabel(v)}
                  </MenuRow>
                );
              })}
            </Menu>
          )}
        </>
      )}
      {view === "chart" && shown && (
        <Chip
          label={intervalLabel(shown)}
          title="Timeframe — always coarser than the chart"
          onClick={() => {
            const next = nextCoarser(shown) ?? "1w";
            onFrequency(next);
          }}
        />
      )}
      <Key
        glyph="◰"
        title={`Corner: ${corner}`}
        onClick={() => cycle(PIP_CORNERS, corner, onCorner)}
      />
      <Key glyph="⤢" title={`Size: ${size}`} onClick={() => cycle(PIP_SIZES, size, onSize)} />
      {onSwap && <Key glyph="⇄" title="Make this the chart's timeframe" onClick={onSwap} />}
      <Key glyph="×" title="Close" onClick={onClose} />
    </div>
  );
}

/**
 * The candles.
 *
 * `stopPropagation` on `pointerdown` as well as click, the guard `ValueLegend`
 * and `PaneDividers` both need: the canvas underneath runs `ZoomPanModifier`, so
 * without it the chart lurches sideways every time somebody touches the panel.
 */
function PipCandles({
  bars,
  width,
  height,
}: {
  bars: readonly Bar[];
  width: number;
  height: number;
}) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const resolve = useTokenResolver();
  const theme = useTheme();

  const paint = useCallback(() => {
    const canvas = ref.current;
    if (!canvas || width <= 0 || height <= 0) return;
    // Device pixels, capped at 2: a thumbnail on a 3× display gains nothing from
    // the third pixel and pays for it on every repaint.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const up = resolve("positive");
    const down = resolve("negative");
    for (const c of candleLayout(bars, width, height).candles) {
      const colour = c.up ? up : down;
      ctx.strokeStyle = colour;
      ctx.fillStyle = colour;
      ctx.lineWidth = 1;
      // Half-pixel offset so a one-pixel wick lands on a pixel rather than
      // across two and renders as a grey smear.
      const x = Math.round(c.x) + 0.5;
      ctx.beginPath();
      ctx.moveTo(x, c.wickTop);
      ctx.lineTo(x, c.wickBottom);
      ctx.stroke();
      ctx.fillRect(c.left, c.top, c.width, c.height);
    }
  }, [bars, width, height, resolve, theme]);

  useEffect(paint, [paint]);

  return (
    <canvas
      ref={ref}
      style={{ display: "block", width, height }}
      onPointerDown={(e) => e.stopPropagation()}
    />
  );
}

/**
 * One registry view, in the PIP's compact form.
 *
 * The panel and a detached window render the *same* component at full size —
 * `compact` is what a view offers instead of being scaled, because a full-size
 * panel shrunk by CSS gives six-point type and a horizontal scrollbar.
 */
function PipView({ view, symbol }: { view: Exclude<PipView, "chart">; symbol: string }) {
  const entry = symbolView(view);
  if (!entry) return null;
  return <entry.Render symbol={symbol} compact />;
}

function Chip({
  label,
  title,
  active,
  chipRef,
  onClick,
}: {
  label: string;
  title: string;
  /** One of a set, and this is the one showing. Omitted for a lone chip. */
  active?: boolean;
  chipRef?: React.Ref<HTMLButtonElement>;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      ref={chipRef}
      title={title}
      // `aria-pressed` rather than `aria-selected`: these are toggle buttons in
      // a row, not options in a listbox, and a listbox would owe the keyboard a
      // roving tabindex this does not implement.
      {...(active === undefined ? {} : { "aria-pressed": active })}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={onClick}
      // Merged here rather than spread into `CHIP_ON` at module level: `CHIP` is
      // declared further down the file, so referencing it there is a temporal
      // dead zone, not a style.
      style={active ? { ...CHIP, ...CHIP_ON } : CHIP}
    >
      {label}
    </button>
  );
}

function Key({
  glyph,
  title,
  onClick,
}: {
  glyph: string;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={onClick}
      style={KEY}
    >
      {glyph}
    </button>
  );
}

const HOST: React.CSSProperties = {
  position: "absolute",
  // Above the value legend and the span controls, below the pane dividers: the
  // panel covers readings, and a divider has to stay grabbable over it.
  zIndex: 5,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: 6,
  border: "1px solid var(--qf-axis)",
  background: "var(--qf-background)",
  boxShadow: "0 6px 24px rgba(0,0,0,0.4)",
};

const HEADER: React.CSSProperties = {
  flex: "0 0 auto",
  height: HEADER_H,
  display: "flex",
  alignItems: "center",
  gap: HEADER_GAP,
  padding: "0 3px 0 4px",
  background: "var(--qf-surface-alt)",
  borderBottom: "1px solid var(--qf-border)",
  // Raised with the panel's own type scale (`usePipScale`). This row is chrome
  // and is sized in pixels rather than `em`, so it does not follow that scale
  // and had to move with it by hand — left at 10 it read as a different, older
  // panel bolted to the top of the new one.
  fontSize: 11,
};

/**
 * The view chips, side by side, with the ones that do not fit behind a chevron.
 *
 * They cycled through one chip until now — one click per view, and no sight of
 * what the other five are. Fine for two, opaque at six.
 *
 * `minWidth: 0` is what actually lets the row shrink inside the header's flex:
 * an `auto` minimum is the content's width, so the strip would refuse to give
 * way and push the chevron and the close button off the end of the header
 * instead of overflowing.
 */
/**
 * The showing view, filled.
 *
 * `--qf-background` as the text colour rather than a literal white or a new
 * `on-accent` token: it is the colour the accent is guaranteed to sit legibly
 * on in every theme, and `GroupBar`'s own `ON` chip already settled this — the
 * two would otherwise invert differently on the White theme.
 */
const CHIP_ON: React.CSSProperties = {
  // An override spread onto `CHIP`, not a whole style — see its use.
  background: "var(--qf-accent)",
  borderColor: "var(--qf-accent)",
  color: "var(--qf-background)",
  fontWeight: 600,
};

const VIEW_STRIP: React.CSSProperties = {
  flex: "1 1 auto",
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  gap: CHIP_GAP,
  // The chips it holds are the ones measured to fit, so nothing should spill —
  // but `hidden` rather than visible, because one mis-measured pixel should clip
  // rather than push the chevron and the close button off the end of the header.
  overflow: "hidden",
};

/**
 * The ruler: off-screen, always every chip.
 *
 * Positioned rather than `display: none`, which has no geometry to measure, and
 * `aria-hidden` because it is a duplicate of the strip beside it.
 */
const RULER: React.CSSProperties = {
  position: "absolute",
  top: -9999,
  left: -9999,
  display: "flex",
  gap: CHIP_GAP,
  visibility: "hidden",
  pointerEvents: "none",
};

const BODY: React.CSSProperties = { flex: 1, minHeight: 0, position: "relative", overflow: "hidden" };
const FILL: React.CSSProperties = { position: "absolute", inset: 0 };

const CHIP: React.CSSProperties = {
  height: 18,
  padding: "0 6px",
  borderRadius: 3,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-surface)",
  color: "var(--qf-text)",
  font: "inherit",
  fontSize: 10.5,
  whiteSpace: "nowrap",
  cursor: "pointer",
};

const KEY: React.CSSProperties = {
  width: 18,
  height: 18,
  padding: 0,
  border: "none",
  borderRadius: 3,
  background: "transparent",
  color: "var(--qf-text-secondary)",
  font: "inherit",
  fontSize: 11,
  lineHeight: 1,
  cursor: "pointer",
};



